// ============================================================
// BOOKINGS ADMIN HOOK - REACT QUERY OPTIMIZED
// ============================================================
// Strategy: React Query with intelligent caching and prefetching
// Benefits: Instant pagination, smart caching, automatic background updates
// Architecture: Same as useInvoicesAdmin for consistency

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { Booking, BookingStatus, PaymentMethod, SpaceType } from '../types/booking.types';

// ===================================================================
// TYPES POUR LA RÉPONSE API (avant transformation)
// ===================================================================
interface BookingApiItem {
  id: string;
  reference: string;
  customerName: string;
  customerEmail: string;
  companyName?: string;
  status: string;
  workflow: string;
  startDate: string;
  endDate?: string;
  totalAmount: number;
  createdAt: string;
  spaceType: {
    id: string;
    name: string;
    slug?: string;
    category?: string;
  } | null;
  transactions: Array<{
    id: string;
    status: string;
    type?: string;
    singpayData?: any;
  }>;
}

// ===================================================================
// CONFIGURATION MAPPINGS
// ===================================================================

const SPACE_TYPE_SLUGS: Record<SpaceType, string[]> = {
  COWORKING: ['espace-coworking', 'coworking'],
  MEETING_ROOM: ['salle-conference', 'salle-de-conference', 'studio-sing', 'studio'],
  PRIVATE_OFFICE: ['bureau-permanent', 'bureau-prive', 'bureau-privatif'],
  EVENT_SPACE: ['espace-evenementiel', 'evenementiel', 'terrasse'],
};

function mapBookingType(spaceType?: any): SpaceType {
  if (!spaceType) return 'COWORKING';

  const category = spaceType.category?.toUpperCase();
  if (category) {
    if (category === 'COWORKING') return 'COWORKING';
    if (category === 'OFFICE' || category === 'PRIVATE_OFFICE') return 'PRIVATE_OFFICE';
    if (category === 'MEETING' || category === 'MEETING_ROOM') return 'MEETING_ROOM';
    if (category === 'EVENT' || category === 'EVENT_SPACE') return 'EVENT_SPACE';
  }

  const slug = spaceType.slug?.toLowerCase();
  if (slug) {
    for (const [type, slugs] of Object.entries(SPACE_TYPE_SLUGS)) {
      if (slugs.includes(slug)) {
        return type as SpaceType;
      }
    }
  }

  return 'COWORKING';
}

function determinePaymentMethod(transactions: BookingApiItem['transactions']): PaymentMethod {
  if (!Array.isArray(transactions) || transactions.length === 0) {
    return 'Non spécifié';
  }

  const successfulTx = transactions.find((tx) => tx.status === 'COMPLETED');
  const targetTx = successfulTx || transactions[transactions.length - 1];

  // Extraire les infos du type de transaction
  const txType = targetTx.type;

  // Mapper les types de transaction vers les méthodes de paiement
  if (txType === 'MOBILE_MONEY') {
    return 'Mobile Money';
  }
  if (txType === 'CARD') {
    return 'Carte bancaire';
  }
  if (txType === 'BANK_TRANSFER') {
    return 'Virement bancaire';
  }

  // Vérifier dans singpayData si disponible
  if (targetTx.singpayData && typeof targetTx.singpayData === 'object') {
    const spData = targetTx.singpayData as any;
    if (spData.paymentMethod) {
      return spData.paymentMethod as PaymentMethod;
    }
    if (spData.provider === 'AIRTEL_MONEY' || spData.airtelMoneyId) {
      return 'Airtel Money';
    }
  }

  return 'SingPay';
}

function transformBookings(items: BookingApiItem[]): Booking[] {
  return items.map((item) => {
    const paymentMethod = determinePaymentMethod(item.transactions);

    return {
      id: item.id,
      ref: item.reference,
      client: item.customerName,
      createdAt: item.createdAt,
      typeValue: mapBookingType(item.spaceType),
      name: item.spaceType?.name || 'Espace SING',
      startDate: item.startDate,
      endDate: item.endDate,
      price: Number(item.totalAmount),
      paymentMethod,
      status: item.status as BookingStatus,
      canQuote: item.workflow === 'QUOTE_REQUIRED',
    };
  });
}

export interface BookingsAdminState {
  bookings: Booking[];
  filteredBookings: Booking[];
  loading: boolean;
  error: string | null;
  pagination: {
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
  stats: {
    total: number;
    pending: number;
    confirmed: number;
    paid: number;
    quoteRequested: number;
    rejected: number;
  };
}

export interface UseBookingsAdminReturn extends BookingsAdminState {
  statusFilter: string;
  typeFilter: string;
  dateFilter: string;
  searchQuery: string;
  hasActiveFilters: boolean;
  updateFilter: (key: string, value: string) => void;
  updateSearch: (query: string) => void;
  resetFilters: () => void;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  refresh: () => void;
}

const DEFAULT_PAGE_SIZE = 10;
const QUERY_KEY_BASE = ['bookings', 'admin'];

interface FetchBookingsResult {
  items: any[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

async function fetchBookings(params: {
  page: number;
  pageSize: number;
  status?: string;
  spaceTypeId?: string;
  search?: string;
}): Promise<FetchBookingsResult> {
  const queryParams = new URLSearchParams({
    page: String(params.page),
    pageSize: String(params.pageSize),
  });

  if (params.status && params.status !== 'all') {
    queryParams.set('status', params.status);
  }
  if (params.spaceTypeId && params.spaceTypeId !== 'all') {
    queryParams.set('spaceTypeId', params.spaceTypeId);
  }
  if (params.search?.trim()) {
    queryParams.set('search', params.search);
  }

  const { data } = await api.get(`/bookings/admin/all?${queryParams.toString()}`);

  if (!data.success) {
    throw new Error(data.error || 'Erreur lors de la récupération des réservations');
  }

  return data.data;
}

export function useBookingsAdmin(): UseBookingsAdminReturn {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const statusFilter = searchParams.get('status') ?? 'all';
  const typeFilter = searchParams.get('type') ?? 'all';
  const dateFilter = searchParams.get('date') ?? 'all';
  const searchQuery = searchParams.get('search') ?? '';
  const page = Number(searchParams.get('page') ?? '1');
  const pageSize = Number(searchParams.get('pageSize') ?? DEFAULT_PAGE_SIZE);

  // Build query key for current filters
  const currentQueryKey = useMemo(
    () => [...QUERY_KEY_BASE, statusFilter, typeFilter, searchQuery, page, pageSize],
    [statusFilter, typeFilter, searchQuery, page, pageSize],
  );

  // React Query with caching and stale time
  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: currentQueryKey,
    queryFn: () =>
      fetchBookings({
        page,
        pageSize,
        status: statusFilter,
        spaceTypeId: typeFilter,
        search: searchQuery,
      }),
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
    placeholderData: (previousData) => previousData, // Keep previous data while loading
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // Prefetch adjacent pages for instant navigation
  const prefetchAdjacentPages = useCallback(
    (currentPage: number, totalPages: number) => {
      const prevPage = currentPage - 1;
      const nextPage = currentPage + 1;

      if (prevPage >= 1) {
        queryClient.prefetchQuery({
          queryKey: [...QUERY_KEY_BASE, statusFilter, typeFilter, searchQuery, prevPage, pageSize],
          queryFn: () =>
            fetchBookings({
              page: prevPage,
              pageSize,
              status: statusFilter,
              spaceTypeId: typeFilter,
              search: searchQuery,
            }),
        });
      }

      if (nextPage <= totalPages) {
        queryClient.prefetchQuery({
          queryKey: [...QUERY_KEY_BASE, statusFilter, typeFilter, searchQuery, nextPage, pageSize],
          queryFn: () =>
            fetchBookings({
              page: nextPage,
              pageSize,
              status: statusFilter,
              spaceTypeId: typeFilter,
              search: searchQuery,
            }),
        });
      }
    },
    [queryClient, statusFilter, typeFilter, searchQuery, pageSize],
  );

  // Prefetch adjacent pages when data loads
  useMemo(() => {
    if (data && !isFetching) {
      prefetchAdjacentPages(page, data.totalPages);
    }
  }, [data, isFetching, page, prefetchAdjacentPages]);

  // Stats - separate query
  const { data: statsData } = useQuery({
    queryKey: [...QUERY_KEY_BASE, 'stats'],
    queryFn: async () => {
      const { data } = await api.get('/bookings/admin/stats');
      return data.success ? data.data : null;
    },
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
  });

  // Transform bookings
  const bookings = useMemo(() => {
    return data?.items ? transformBookings(data.items as BookingApiItem[]) : [];
  }, [data?.items]);

  const filteredBookings = bookings;

  const totalPages = data?.totalPages ?? 0;
  const total = data?.total ?? 0;

  const stats = useMemo(
    () => ({
      total: statsData?.total ?? 0,
      pending: statsData?.pending ?? 0,
      confirmed: statsData?.confirmed ?? 0,
      paid: statsData?.paid ?? 0,
      quoteRequested: statsData?.quoteRequested ?? 0,
      rejected: statsData?.rejected ?? 0,
    }),
    [statsData],
  );

  // Invalidate cache when filters change
  const invalidateBookings = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: QUERY_KEY_BASE });
  }, [queryClient]);

  // Actions
  const updateFilter = useCallback(
    (key: string, value: string) => {
      setSearchParams((prev) => {
        if (value === 'all') {
          prev.delete(key);
        } else {
          prev.set(key, value);
        }
        prev.set('page', '1');
        return prev;
      });
      invalidateBookings();
    },
    [setSearchParams, invalidateBookings],
  );

  const updateSearch = useCallback(
    (query: string) => {
      setSearchParams((prev) => {
        if (query.trim()) {
          prev.set('search', query);
        } else {
          prev.delete('search');
        }
        prev.set('page', '1');
        return prev;
      });
      invalidateBookings();
    },
    [setSearchParams, invalidateBookings],
  );

  const resetFilters = useCallback(() => {
    setSearchParams({});
    invalidateBookings();
  }, [setSearchParams, invalidateBookings]);

  const setPage = useCallback(
    (newPage: number) => {
      setSearchParams((prev) => {
        prev.set('page', String(newPage));
        return prev;
      });
    },
    [setSearchParams],
  );

  const setPageSize = useCallback(
    (size: number) => {
      setSearchParams((prev) => {
        prev.set('pageSize', String(size));
        prev.set('page', '1');
        return prev;
      });
    },
    [setSearchParams],
  );

  const refresh = useCallback(() => {
    invalidateBookings();
  }, [invalidateBookings]);

  const hasActiveFilters = useMemo(() => {
    return statusFilter !== 'all' || typeFilter !== 'all' || dateFilter !== 'all' || searchQuery.trim() !== '';
  }, [statusFilter, typeFilter, dateFilter, searchQuery]);

  return {
    bookings,
    filteredBookings,
    loading: isLoading,
    error: error ? error.message : null,
    pagination: {
      total,
      page,
      pageSize,
      totalPages,
    },
    stats,
    statusFilter,
    typeFilter,
    dateFilter,
    searchQuery,
    hasActiveFilters,
    updateFilter,
    updateSearch,
    resetFilters,
    setPage,
    setPageSize,
    refresh,
  };
}
